import BulletList from '@tiptap/extension-bullet-list';
import Heading from '@tiptap/extension-heading';
import ListItem from '@tiptap/extension-list-item';
import ListKeymap from '@tiptap/extension-list-keymap';
import { Mention } from '@tiptap/extension-mention';
import OrderedList from '@tiptap/extension-ordered-list';
import Placeholder from '@tiptap/extension-placeholder';
import Typography from '@tiptap/extension-typography';
import Underline from '@tiptap/extension-underline';
import { AnyExtension } from '@tiptap/react';
import StarterKit, { type StarterKitOptions } from '@tiptap/starter-kit';

import { RteDropCursor } from '../drop-cursor';
import { RteImage } from '../image';
import { Link } from '../link';
import getSuggestions from '../mentions/suggestions';
import SlashCommand from '../slash-command';
import {
	RTE_EXTENSIONS,
	UploadImageFn,
	VariableItem,
	VariableProps,
} from '../types/rich-text-editor';
import { createVariableExtension } from '../variables/variable-extension';
import { RteYoutube } from '../youtube';
import { YOUTUBE_EMBED_HEIGHT, YOUTUBE_EMBED_WIDTH } from '../youtube/constants';

interface GetExtensionProps {
	placeholder?: string;
	variables?: VariableItem[];
	getVariableTooltip?: (item: VariableItem) => string;
	triggerChar?: string;
	name?: string;
	pluginKeyName?: string;
	includeVariableNode?: boolean;
	variableProps?: VariableProps;
	uploadImage?: UploadImageFn;
	onRequestYoutubeUrl?: () => void;
	starterKitOptions?: Partial<StarterKitOptions>;
}

export const RteExtensionMap: Record<RTE_EXTENSIONS, (props?: GetExtensionProps) => AnyExtension> =
	{
		[RTE_EXTENSIONS.STARTER_KIT]: (props?: GetExtensionProps) =>
			StarterKit.configure({
				heading: false,
				orderedList: false,
				bulletList: false,
				// `RteDropCursor` replaces it; the bundled one flickers while dragging.
				dropcursor: false,
				...props?.starterKitOptions,
			}),
		[RTE_EXTENSIONS.SLASH_COMMAND]: () => SlashCommand,
		[RTE_EXTENSIONS.LINK]: () =>
			Link.configure({
				openOnClick: true,
				linkOnPaste: true,
				defaultProtocol: 'https',
				HTMLAttributes: {
					class: 'text-blue-600 underline-none hover:underline',
				},
				protocols: ['http', 'https', 'mailto', 'tel'],
			}),
		[RTE_EXTENSIONS.UNDERLINE]: () => Underline,
		[RTE_EXTENSIONS.ORDERED_LIST]: () =>
			OrderedList.configure({
				HTMLAttributes: {
					class: 'list-decimal leading-6 mt-2',
				},
				keepMarks: true,
				keepAttributes: true,
			}),
		[RTE_EXTENSIONS.BULLET_LIST]: () =>
			BulletList.configure({
				HTMLAttributes: {
					class: 'list-disc leading-6 mt-2',
				},
				keepMarks: true,
				keepAttributes: true,
			}),
		[RTE_EXTENSIONS.LIST_ITEM]: () => ListItem,
		[RTE_EXTENSIONS.LIST_KEYMAP]: () => ListKeymap,
		[RTE_EXTENSIONS.HEADING]: () =>
			Heading.configure({
				levels: [1, 2, 3],
				HTMLAttributes: {
					class: 'mt-6 first:mt-0',
				},
			}),
		[RTE_EXTENSIONS.PLACEHOLDER]: (props?: GetExtensionProps) =>
			Placeholder.configure({
				placeholder: props?.placeholder ?? 'Add a description',
				emptyEditorClass: 'is-editor-empty',
			}),
		[RTE_EXTENSIONS.MENTIONS]: () =>
			Mention.configure({
				HTMLAttributes: {
					class: 'mention',
				},
				suggestion: getSuggestions(),
			}),
		[RTE_EXTENSIONS.TYPOGRAPHY]: () =>
			Typography.configure({
				emDash: '—',
				ellipsis: '…',
				openDoubleQuote: false,
				closeDoubleQuote: false,
				openSingleQuote: false,
				closeSingleQuote: false,
			}),
		[RTE_EXTENSIONS.VARIABLE]: (props?: GetExtensionProps) =>
			createVariableExtension(props?.name).configure({
				variables: props?.variables || [],
				triggerChar: props?.triggerChar,
				pluginKeyName: props?.pluginKeyName,
				includeVariableNode: props?.includeVariableNode,
				variableProps: props?.variableProps,
			}),
		// Always loaded so saved images still render; no uploader just means no way to insert one.
		[RTE_EXTENSIONS.IMAGE]: (props?: GetExtensionProps) =>
			RteImage.configure({ uploadImage: props?.uploadImage }),
		[RTE_EXTENSIONS.YOUTUBE]: (props?: GetExtensionProps) =>
			RteYoutube.configure({
				// Keeps embeds on youtube-nocookie.com, which the sanitizer's host allow-list includes.
				nocookie: true,
				width: YOUTUBE_EMBED_WIDTH,
				height: YOUTUBE_EMBED_HEIGHT,
				onRequestUrl: props?.onRequestYoutubeUrl,
			}),
		[RTE_EXTENSIONS.DROP_CURSOR]: () => RteDropCursor,
	};
